import { App, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { DiagnosticRuleService } from '~/app/services/diagnosticRules/diagnostic-rule.service';
import { DiagnosticRuleHttpError } from '~/app/services/diagnosticRules/diagnostic-rule-error';
import type {
  DiagnosticRule,
  DiagnosticRuleFilters,
  DiagnosticRuleInput,
  DiagnosticRuleScope,
} from '~/app/services/diagnosticRules/diagnostic-rule.types';
import {
  ruleAbilities,
  rulesRefetchInterval,
  needsCrossTenantBypass,
} from '../utils';

export type RuleDrawerState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; rule: DiagnosticRule };

function isUnauthorized(error: Error): boolean {
  return error instanceof DiagnosticRuleHttpError && error.status === 401;
}

export function useDiagnosticRulesPage() {
  const { t } = useTranslation('diagnosticRules');
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const { canManage } = ruleAbilities(hasPermission);
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;

  // Super admin: "global" (EVIMO rules) or any tenant; others: their own org.
  const [scope, setScope] = useState<DiagnosticRuleScope>('global');
  const [filters, setFilters] = useState<DiagnosticRuleFilters>({});
  const [drawer, setDrawer] = useState<RuleDrawerState>({ mode: 'closed' });
  const [detailRuleId, setDetailRuleId] = useState<string | null>(null);

  const listScope = isSuperAdmin ? scope : null;
  const crossTenantBypass = needsCrossTenantBypass(
    isSuperAdmin,
    scope,
    user?.organizationId,
  );
  const listQuery = useQuery({
    queryKey: ['diagnostic-rules', listScope, filters],
    queryFn: () =>
      DiagnosticRuleService.list(listScope, filters, crossTenantBypass),
    refetchInterval: (query) =>
      rulesRefetchInterval(query.state.data, query.state.error),
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 2,
  });

  const optionsQuery = useQuery({
    queryKey: ['diagnostic-rules', 'options', listScope],
    queryFn: () => DiagnosticRuleService.options(listScope, crossTenantBypass),
    staleTime: 5 * 60_000,
  });

  const organizationsQuery = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: () => OrganizationService.findAllOrganizations(),
    enabled: isSuperAdmin,
    staleTime: 5 * 60_000,
  });

  const detailQuery = useQuery({
    queryKey: ['diagnostic-rules', 'detail', detailRuleId],
    queryFn: () =>
      DiagnosticRuleService.get(detailRuleId as string, crossTenantBypass),
    enabled: Boolean(detailRuleId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['diagnostic-rules'] });
  const onError = (error: Error) =>
    message.error(error.message || t('messages.genericError'));

  const saveMutation = useMutation({
    mutationFn: (input: DiagnosticRuleInput) =>
      drawer.mode === 'edit'
        ? DiagnosticRuleService.update(drawer.rule.id, input, crossTenantBypass)
        : DiagnosticRuleService.create(
            input,
            isSuperAdmin && scope !== 'global' ? scope : undefined,
            crossTenantBypass,
          ),
    onSuccess: async () => {
      message.success(
        t(drawer.mode === 'edit' ? 'messages.updated' : 'messages.created'),
      );
      setDrawer({ mode: 'closed' });
      await invalidate();
    },
    onError,
  });

  const activeMutation = useMutation({
    mutationFn: ({ rule, active }: { rule: DiagnosticRule; active: boolean }) =>
      DiagnosticRuleService.setActive(rule.id, active, crossTenantBypass),
    onSuccess: async (_rule, { active }) => {
      message.success(
        t(active ? 'messages.activated' : 'messages.deactivated'),
      );
      await invalidate();
    },
    onError,
  });

  const reindexMutation = useMutation({
    mutationFn: (rule: DiagnosticRule) =>
      DiagnosticRuleService.reindex(rule.id, crossTenantBypass),
    onSuccess: async () => {
      message.success(t('messages.reindexed'));
      await invalidate();
    },
    onError,
  });

  const confirmSetActive = (rule: DiagnosticRule, active: boolean) => {
    Modal.confirm({
      title: t(active ? 'confirm.activateTitle' : 'confirm.deactivateTitle'),
      content: t(active ? 'confirm.activateBody' : 'confirm.deactivateBody', {
        title: rule.title,
      }),
      okText: t(active ? 'actions.activate' : 'actions.deactivate'),
      cancelText: t('actions.cancel'),
      onOk: () => activeMutation.mutateAsync({ rule, active }),
    });
  };

  return {
    isSuperAdmin,
    canManage,
    scope,
    onScopeChange: setScope,
    organizations: organizationsQuery.data ?? [],
    filters,
    onFiltersChange: setFilters,
    rules: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    onRefresh: () => listQuery.refetch(),
    options: optionsQuery.data ?? {
      assetModels: [],
      manufacturers: [],
      firmwares: [],
    },
    drawer,
    onCreate: () => setDrawer({ mode: 'create' }),
    onEdit: (rule: DiagnosticRule) => setDrawer({ mode: 'edit', rule }),
    onCloseDrawer: () => setDrawer({ mode: 'closed' }),
    onSave: (input: DiagnosticRuleInput) => saveMutation.mutateAsync(input),
    isSaving: saveMutation.isPending,
    onSetActive: confirmSetActive,
    onReindex: (rule: DiagnosticRule) => reindexMutation.mutate(rule),
    isDetailOpen: detailRuleId !== null,
    detail: detailQuery.data ?? null,
    isDetailLoading: detailQuery.isLoading,
    onOpenDetail: setDetailRuleId,
    onCloseDetail: () => setDetailRuleId(null),
  };
}

export type DiagnosticRulesPageModel = ReturnType<
  typeof useDiagnosticRulesPage
>;
